import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { usePathname } from "next/navigation";

import { useConsoleSideBarActiveItemsIndicator } from "pages/[tenant]/console/hooks/useConsoleSideBarActiveItemsIndicator";
import { useSideBarSections } from "pages/[tenant]/console/hooks/useSideBarSections";
import {
  IConsolePageContext,
  IGetCurrentSectionInfoByCurrentPathResult,
  IOption,
  IOptionsList,
  ISection,
} from "pages/[tenant]/console/types";
import { IGroup } from "pages/[tenant]/g/@types";

import { useFeature } from "shared/utils/hooks/useFeature";

import { consoleHelpDeskService } from "@Console/help-desk/services/consoleHelpDeskService";
import { consoleTaskService } from "@Console/tasks/services/consoleTaskService";
import { captureException, captureMessage } from "@sentry/nextjs";

export const ConsolePageContext = createContext<IConsolePageContext>(
  {} as IConsolePageContext,
);

export const ConsolePageContextProvider = ({ children }) => {
  const isTaskManagementEnabled = useFeature("f_assignments_management");
  const isConsoleHelpDeskEnabled = useFeature("f_call_center");

  const [amountOfUnrevisedTasks, setAmountOfUnrevisedTasks] = useState(0);
  const [amountOfCancellationTicketsOpen, setAmountOfCancellationTicketsOpen] =
    useState(0);

  const [currentGroup, setCurrentGroup] = useState<IGroup | null>(null);
  const { sectionInfo } = useSideBarSections({
    amountOfUnrevisedTasks,
    amountOfCancellationTicketsOpen,
  });
  const optionsList = useMemo<IOptionsList>(
    () =>
      Object.entries(sectionInfo).reduce(
        (acc, [_, option]) => ({
          ...acc,
          [option.label]: {
            options: option.options,
            subSections: option?.subSections,
            customSectionLabel: option?.customSectionLabel,
          },
        }),
        {},
      ),
    [sectionInfo],
  );

  const {
    setInfoToMoveActiveSectionIndicator,
    activeSectionPosition,
    activeOptionPosition,
    setInfoToMoveActiveOptionIndicator,
  } = useConsoleSideBarActiveItemsIndicator();

  const pathname = usePathname();

  const [activeOptionPathInRealTime, setActiveOptionPathInRealTime] =
    useState(pathname);
  const [activeSectionLabel, setActiveSectionLabel] = useState("");
  const isInConsolePage = pathname?.includes("/console");

  const [isMobileSectionsDropdownOpen, setIsMobileSectionsDropdownOpen] =
    useState(false);
  const [
    isMobileSectionOptionsDropdownOpen,
    setIsMobileSectionOptionsDropdownOpen,
  ] = useState(false);

  const [
    isMobileInternalSectionOptionsDropdownOpen,
    setIsMobileInternalSectionOptionsDropdownOpen,
  ] = useState(false);

  const openMobileSectionsDropdown = () => {
    if (isMobileSectionOptionsDropdownOpen) closeMobileSectionOptionsDropdown();
    if (isMobileInternalSectionOptionsDropdownOpen)
      closeMobileInternalSectionsDropdown();

    setIsMobileSectionsDropdownOpen(true);
  };

  const closeMobileSectionsDropdown = () =>
    setIsMobileSectionsDropdownOpen(false);

  const openMobileSectionOptionsDropdown = () => {
    if (isMobileSectionsDropdownOpen) closeMobileSectionsDropdown();
    if (isMobileInternalSectionOptionsDropdownOpen)
      closeMobileInternalSectionsDropdown();

    setIsMobileSectionOptionsDropdownOpen(true);
  };

  const closeMobileSectionOptionsDropdown = () =>
    setIsMobileSectionOptionsDropdownOpen(false);

  const openMobileInternalSectionsDropdown = () => {
    if (isMobileSectionOptionsDropdownOpen) closeMobileSectionOptionsDropdown();
    if (isMobileSectionsDropdownOpen) closeMobileSectionsDropdown();

    setIsMobileInternalSectionOptionsDropdownOpen(true);
  };

  const closeMobileInternalSectionsDropdown = () =>
    setIsMobileInternalSectionOptionsDropdownOpen(false);

  const changeActiveSection = (label: string) => setActiveSectionLabel(label);
  const clearActiveSection = () => setActiveSectionLabel("");

  const getActiveOptionInView = (currentSectionOptions: ISection): IOption => {
    try {
      const currentSectionHasOptions =
        currentSectionOptions?.options || currentSectionOptions?.subSections;
      if (!currentSectionHasOptions) return;

      const { options, subSections } = currentSectionOptions;
      const activeOption =
        options?.find((option) => option?.path === pathname) ||
        subSections
          ?.find((subSection) =>
            subSection?.options?.find((option) => option?.path === pathname),
          )
          ?.options?.find((option) => option?.path === pathname);

      return activeOption;
    } catch (error) {
      console.log("Error in getActiveOptionInView:", error);
      captureException(error);
      captureMessage("Error in getActiveOptionInView");
    }
  };

  const getCurrentSectionInfoByCurrentPath = useCallback(
    (currentPath: string): IGetCurrentSectionInfoByCurrentPathResult => {
      const [currentSectionName, currentSectionOptions] = Object.entries(
        optionsList,
      ).find(
        ([_, value]) =>
          value?.options?.some((option) => option?.path === currentPath) ||
          value?.options?.some((option) =>
            option?.path?.includes(currentPath),
          ) ||
          value?.subSections?.some(
            (option) =>
              option?.options?.some((option) => option?.path === currentPath) ||
              option?.options?.some((option) =>
                option?.path?.includes(currentPath),
              ),
          ),
      ) || [""];

      return {
        currentSectionName,
        currentSectionOptions,
        customSectionLabel: currentSectionOptions?.customSectionLabel
          ? () => currentSectionOptions?.customSectionLabel()
          : null,
      };
    },
    [optionsList],
  );

  useEffect(() => {
    if (!optionsList) return;

    const { currentSectionName } = getCurrentSectionInfoByCurrentPath(pathname);
    if (!!currentSectionName) changeActiveSection(currentSectionName);
    if (!!pathname) setActiveOptionPathInRealTime(pathname);
  }, [optionsList, pathname]);

  const handleChangeActiveSection = ({ currentTarget }) => {
    const sectionLabel = currentTarget?.getAttribute("data-label");

    changeActiveSection(sectionLabel);
    setInfoToMoveActiveSectionIndicator(currentTarget);
  };

  const clearOptionPathInRealTime = () => setActiveOptionPathInRealTime("");

  const getAmountOfUnrevisedTasks = useCallback(async () => {
    try {
      const response = await consoleTaskService.getAmountOfUnrevisedTasks({});
      const amountOfUnrevisedTasks = response?.data?.pending_assignments;
      setAmountOfUnrevisedTasks(amountOfUnrevisedTasks);
    } catch (error) {
      captureException(error);
      captureMessage(
        "Error ~ useGroupAndContentOptions::getAmountOfUnrevisedTasks",
      );
      return null;
    }
  }, []);

  const getAmountOfOpenTickets = useCallback(async () => {
    try {
      if (isConsoleHelpDeskEnabled) {
        const response = await consoleHelpDeskService.getConsoleOpenTickets();
        setAmountOfCancellationTicketsOpen(response?.open_tickets);
        return;
      }
    } catch (error) {
      captureException(error);
      captureMessage(
        "Error ~ useGroupAndContentOptions::getAmountOfUnrevisedTasks",
      );
      return null;
    }
  }, [isConsoleHelpDeskEnabled]);

  const fetchSideBarBadges = useCallback(async () => {
    if (isInConsolePage) {
      if (isTaskManagementEnabled) await getAmountOfUnrevisedTasks();
      await getAmountOfOpenTickets();
    }
  }, [
    getAmountOfOpenTickets,
    getAmountOfUnrevisedTasks,
    isInConsolePage,
    isTaskManagementEnabled,
  ]);

  useEffect(() => {
    fetchSideBarBadges();
  }, [fetchSideBarBadges]);

  return (
    <ConsolePageContext.Provider
      value={{
        optionsList,
        changeActiveSection,
        activeSectionLabel,
        clearActiveSection,
        getCurrentSectionInfoByCurrentPath,
        sectionInfo,
        setInfoToMoveActiveSectionIndicator,
        handleChangeActiveSection,
        activeOptionPathInRealTime,
        setActiveOptionPathInRealTime,
        activeSectionPosition,
        getActiveOptionInView,
        isMobileSectionsDropdownOpen,
        isMobileSectionOptionsDropdownOpen,
        openMobileSectionsDropdown,
        closeMobileSectionsDropdown,
        openMobileSectionOptionsDropdown,
        closeMobileSectionOptionsDropdown,
        activeOptionPosition,
        setInfoToMoveActiveOptionIndicator,
        clearOptionPathInRealTime,
        isMobileInternalSectionOptionsDropdownOpen,
        openMobileInternalSectionsDropdown,
        closeMobileInternalSectionsDropdown,
        setAmountOfUnrevisedTasks,
        amountOfUnrevisedTasks,
        currentGroup,
        setCurrentGroup,
        amountOfCancellationTicketsOpen,
        setAmountOfCancellationTicketsOpen,
      }}
    >
      {children}
    </ConsolePageContext.Provider>
  );
};
